/**
 * RestCatalogSource — `ICatalogSource` HTTP client for the forge-store REST backend.
 *
 * Wire shape:
 *
 *   GET  /api/catalog/get?ref=<publisher>/<name>@<version>   → asset row | null
 *   GET  /api/catalog/list?publisher=&kind=&category=         → asset row[]
 *   GET  /api/tarball/manifest?sha256=<hex>                   → { sha256, url, sizeBytes, fileCount, public: true }
 *   GET  <manifest.url>                                        → binary tarball (follows 307 redirect)
 *
 * The forge-store REST API is served by Nuxt 4 + Nitro (`forge-store/server/api/`).
 * All catalog endpoints are anonymous (no authentication required). The tarball
 * download URL may be a presigned S3 URL or a CDN URL; the client follows the
 * redirect and downloads the bytes directly.
 *
 * The client verifies SHA256 on the client side after download (spec invariant:
 * "Hash pinning ... Mismatch = hard fail").
 *
 * Cache / air-gapped: identical semantics to {@link RemoteCatalogSource} — stale-while-
 * revalidate on metadata, content-addressed tarballs cached forever.
 *
 * @docLink packages/library/api-reference#rest-catalog-source
 */

import { parseAssetRef } from "@skaile/workspaces/core";
import type {
  CatalogAsset,
  CatalogAssetFilter,
  CatalogSourceInfo,
  ICatalogSource,
} from "@skaile/workspaces/plugins";

import { CatalogCache, type CatalogCacheOptions, sha256Hex } from "./cache.js";
import {
  CatalogHttpError,
  type HttpClient,
  OfflineError,
  TarballHashMismatchError,
} from "./remote-catalog-source.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Constructor options for {@link RestCatalogSource}.
 *
 * @docLink packages/library/api-reference#rest-catalog-source
 */
export interface RestCatalogSourceOptions {
  /** Base URL of the forge-store instance, e.g. `http://localhost:3000`. No trailing slash required. */
  baseUrl: string;
  /**
   * Metadata cache TTL in milliseconds.
   * Default `86_400_000` (24h). `0` disables network — air-gapped mode.
   */
  cacheTtlMs?: number;
  /** Override `fetch` for tests. */
  httpClient?: HttpClient;
  /** Override the cache directory. Default is `~/.skaile/cache/catalog/`. */
  cacheDir?: string;
  /** Override clock for tests. */
  now?: () => number;
  /** Pre-built cache instance (test injection). When provided, `cacheTtlMs`/`cacheDir`/`now` are ignored. */
  cache?: CatalogCache;
  /**
   * If true, suppresses the background revalidation `unhandledRejection` log when
   * a stale-while-revalidate fetch fails. Default false.
   */
  silentBackgroundErrors?: boolean;
}

// ---------------------------------------------------------------------------
// RestCatalogSource
// ---------------------------------------------------------------------------

const DEFAULT_TTL_MS = 86_400_000; // 24h

/**
 * HTTP client implementation of `ICatalogSource` for the forge-store REST API.
 * See module docstring for the wire contract.
 *
 * @docLink packages/library/api-reference#rest-catalog-source
 */
export class RestCatalogSource implements ICatalogSource {
  readonly id: string;
  readonly baseUrl: string;
  readonly cache: CatalogCache;
  private readonly cacheTtlMs: number;
  private readonly httpClient: HttpClient;
  private readonly silentBackgroundErrors: boolean;
  /** Tracks in-flight background revalidations so callers can `await` them in tests. */
  private readonly inFlight = new Map<string, Promise<void>>();

  constructor(opts: RestCatalogSourceOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.id = `rest:${this.baseUrl}`;
    this.cacheTtlMs = opts.cacheTtlMs ?? DEFAULT_TTL_MS;
    this.httpClient = opts.httpClient ?? defaultFetch;
    this.silentBackgroundErrors = opts.silentBackgroundErrors ?? false;
    this.cache =
      opts.cache ??
      new CatalogCache({
        dir: opts.cacheDir,
        ttlMs: this.cacheTtlMs,
        now: opts.now,
      } satisfies CatalogCacheOptions);
  }

  // ── ICatalogSource ──────────────────────────────────────────────────────

  /**
   * Resolve a single asset by canonical ref (`<publisher>/<name>@<version>`).
   *
   * Cache: hit returns immediately. Miss fetches `/api/catalog/get?ref=<ref>`. Stale
   * entry serves the cached value and triggers a background refresh.
   *
   * Air-gapped: throws {@link OfflineError} on cache miss.
   */
  async resolve(ref: string): Promise<CatalogAsset | null> {
    const fresh = this.cache.getResolve(ref);
    if (fresh) return fresh.value;

    if (this.cache.airGapped) {
      const stale = this.cache.getResolveStale(ref);
      if (stale) return stale.value;
      throw new OfflineError(`Asset '${ref}' not in cache`);
    }

    // Stale-while-revalidate: serve stale + refresh in background.
    const stale = this.cache.getResolveStale(ref);
    if (stale) {
      this.scheduleBackground(`resolve:${ref}`, () => this.fetchAndStoreResolve(ref));
      return stale.value;
    }

    return this.fetchAndStoreResolve(ref);
  }

  /**
   * List assets matching `filter`.
   *
   * Cache: hit returns immediately. Miss fetches `/api/catalog/list`. Stale
   * entry serves the cached array and triggers a background refresh.
   *
   * Air-gapped: throws {@link OfflineError} on cache miss.
   *
   * Note: `filter.prefix` is not supported by the forge-store REST API and is silently ignored.
   */
  async listAssets(filter?: CatalogAssetFilter): Promise<CatalogAsset[]> {
    const fresh = this.cache.getList(filter);
    if (fresh) return fresh.items;

    if (this.cache.airGapped) {
      const stale = this.cache.getListStale(filter);
      if (stale) return stale.items;
      throw new OfflineError("Catalog list not in cache");
    }

    const stale = this.cache.getListStale(filter);
    if (stale) {
      const filterKey = JSON.stringify(filter ?? null);
      this.scheduleBackground(`list:${filterKey}`, () => this.fetchAndStoreList(filter));
      return stale.items;
    }

    return this.fetchAndStoreList(filter);
  }

  /**
   * Fetch the content-addressed tarball for an asset.
   *
   * Steps:
   * 1. `GET /api/tarball/manifest?sha256=<sha256>` → JSON with `{ url, ... }`
   * 2. `GET <manifest.url>` following redirects → raw bytes
   * 3. Verify SHA256 client-side — hard fail on mismatch
   * 4. Write to cache on success
   *
   * Cache: tarballs are content-addressed and cached forever once written.
   *
   * Air-gapped: throws {@link OfflineError} on cache miss.
   *
   * @throws {@link TarballHashMismatchError} when the downloaded SHA256 does not match.
   */
  async fetchTarball(_ref: string, sha256: string): Promise<Uint8Array> {
    const cached = this.cache.readTarball(sha256);
    if (cached) {
      // Defensive: re-verify the cached blob. A bit-flip on disk should not
      // poison the install path. (Cheap — local file read, ~ms.)
      const actual = sha256Hex(cached);
      if (actual !== sha256) {
        throw new TarballHashMismatchError(sha256, actual);
      }
      return cached;
    }

    if (this.cache.airGapped) {
      throw new OfflineError(`Tarball '${sha256}' not in cache`);
    }

    // Step 1: fetch the manifest to get the download URL.
    const manifestUrl = `${this.baseUrl}/api/tarball/manifest?sha256=${encodeURIComponent(sha256)}`;
    const manifestRes = await this.httpClient(manifestUrl, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!manifestRes.ok) {
      throw new CatalogHttpError(
        `Failed to fetch tarball manifest for ${sha256}: ${manifestRes.status} ${manifestRes.statusText}`,
        manifestRes.status,
        manifestUrl,
      );
    }
    const manifestText = await manifestRes.text();
    let manifest: unknown;
    try {
      manifest = JSON.parse(manifestText);
    } catch {
      throw new CatalogHttpError(
        `Tarball manifest response is not valid JSON: ${manifestText.slice(0, 200)}`,
        manifestRes.status,
        manifestUrl,
      );
    }
    const downloadUrl =
      manifest && typeof manifest === "object" && "url" in (manifest as object)
        ? (manifest as { url: unknown }).url
        : undefined;
    if (typeof downloadUrl !== "string") {
      throw new CatalogHttpError(
        `Tarball manifest missing 'url' field`,
        manifestRes.status,
        manifestUrl,
      );
    }

    // Step 2: download the tarball bytes.
    const tarballRes = await this.httpClient(downloadUrl, { method: "GET", redirect: "follow" });
    if (!tarballRes.ok) {
      throw new CatalogHttpError(
        `Failed to fetch tarball ${sha256}: ${tarballRes.status} ${tarballRes.statusText}`,
        tarballRes.status,
        downloadUrl,
      );
    }
    const buf = new Uint8Array(await tarballRes.arrayBuffer());

    // Step 3: verify SHA256.
    const actual = sha256Hex(buf);
    if (actual !== sha256) {
      throw new TarballHashMismatchError(sha256, actual);
    }

    // Step 4: write to cache only after verification.
    this.cache.writeTarball(sha256, buf);
    return buf;
  }

  /**
   * Enumerate available versions for a version-less ref. The forge-store REST
   * API has no version-list route, so this derives versions from `listAssets`
   * (filtered to the publisher) and keeps only the matching kind+name. Reuses
   * the cached list path, so repeated calls are cheap.
   */
  async listVersions(ref: string): Promise<string[]> {
    const parsed = parseAssetRef(ref);
    // Stored publishers may or may not carry the `@` scope sigil — compare both
    // the wire filter and the in-memory match without it.
    const wantPublisher = parsed.publisher?.replace(/^@/, "");
    const assets = await this.listAssets(wantPublisher ? { publisher: wantPublisher } : undefined);
    return assets
      .filter(
        (a) =>
          a.kind === parsed.kind &&
          a.name === parsed.name &&
          (wantPublisher === undefined || a.publisher.replace(/^@/, "") === wantPublisher),
      )
      .map((a) => a.version);
  }

  /**
   * Single synthetic Source pointing at the configured forge-store URL.
   */
  async listSources(): Promise<CatalogSourceInfo[]> {
    return [{ id: this.id, type: "rest-catalog", url: this.baseUrl }];
  }

  // ── extension API ───────────────────────────────────────────────────────

  /**
   * Force a metadata-cache refresh. Wipes all resolve/list entries and
   * re-fetches `listAssets()` (no filter) to pre-warm the canonical query.
   *
   * Driven by `skaile update`. Tarballs (content-addressed) are not touched.
   *
   * In air-gapped mode this method **does** make a network call (bypassing
   * the TTL=0 gate); it is the only way to repopulate the cache.
   */
  async refresh(): Promise<{ assetsCached: number }> {
    this.cache.invalidateAll();
    // Air-gapped bypass: call the network directly even when ttl is 0.
    const items = await this.fetchListNetwork(undefined);
    this.cache.setList(undefined, items);
    // Pre-warm resolve cache for known refs so subsequent reads are hot.
    for (const item of items) {
      this.cache.setResolve(item.id, item);
    }
    return { assetsCached: items.length };
  }

  /**
   * Wait for any in-flight stale-while-revalidate background fetches to settle.
   * Test-only escape hatch.
   */
  async waitForBackground(): Promise<void> {
    // Snapshot to avoid mutation during iteration.
    const promises = Array.from(this.inFlight.values());
    await Promise.allSettled(promises);
  }

  // ── private ─────────────────────────────────────────────────────────────

  private scheduleBackground(key: string, fn: () => Promise<unknown>): void {
    if (this.inFlight.has(key)) return;
    const p = (async () => {
      try {
        await fn();
      } catch (err) {
        if (!this.silentBackgroundErrors) {
          process.stderr.write(
            `[skaile/catalog] Background refresh failed for ${key}: ${err instanceof Error ? err.message : String(err)}\n`,
          );
        }
      } finally {
        this.inFlight.delete(key);
      }
    })();
    this.inFlight.set(key, p);
  }

  private async fetchAndStoreResolve(ref: string): Promise<CatalogAsset | null> {
    const value = await this.fetchResolveNetwork(ref);
    this.cache.setResolve(ref, value);
    return value;
  }

  private async fetchAndStoreList(filter?: CatalogAssetFilter): Promise<CatalogAsset[]> {
    const items = await this.fetchListNetwork(filter);
    this.cache.setList(filter, items);
    return items;
  }

  /** Raw network call: GET /api/catalog/get?ref=<ref> */
  private async fetchResolveNetwork(ref: string): Promise<CatalogAsset | null> {
    const url = `${this.baseUrl}/api/catalog/get?ref=${encodeURIComponent(ref)}`;
    const res = await this.httpClient(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new CatalogHttpError(
        `catalog/get ${ref} failed: ${res.status} ${res.statusText}`,
        res.status,
        url,
      );
    }
    const data = await parseRestResponse(res, url);
    if (data === null || data === undefined) return null;
    return normalizeRestAsset(data);
  }

  /** Raw network call: GET /api/catalog/list?publisher=&kind=&category= */
  private async fetchListNetwork(filter?: CatalogAssetFilter): Promise<CatalogAsset[]> {
    const params = new URLSearchParams();
    if (filter?.publisher) params.set("publisher", filter.publisher);
    if (filter?.kind) params.set("kind", filter.kind);
    // Note: filter.prefix is not supported by forge-store REST API and is omitted.
    // filter.category is also not on CatalogAssetFilter — category filtering is server-side only.
    const qs = params.toString();
    const url = `${this.baseUrl}/api/catalog/list${qs ? `?${qs}` : ""}`;
    const res = await this.httpClient(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new CatalogHttpError(
        `catalog/list failed: ${res.status} ${res.statusText}`,
        res.status,
        url,
      );
    }
    const data = await parseRestResponse(res, url);
    if (!Array.isArray(data)) {
      throw new CatalogHttpError(
        `catalog/list returned non-array payload (got ${typeof data})`,
        200,
        url,
      );
    }
    return data.map(normalizeRestAsset);
  }
}

// ---------------------------------------------------------------------------
// REST response parsing
// ---------------------------------------------------------------------------

/**
 * Parse a forge-store REST JSON response. Throws {@link CatalogHttpError} when
 * the response body is not valid JSON.
 */
async function parseRestResponse(res: Response, url: string): Promise<unknown> {
  const text = await res.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    throw new CatalogHttpError(
      `REST response is not valid JSON: ${text.slice(0, 200)}`,
      res.status,
      url,
    );
  }
}

// ---------------------------------------------------------------------------
// Server → CatalogAsset normalization
// ---------------------------------------------------------------------------

/**
 * Normalize a forge-store REST asset row to {@link CatalogAsset}.
 *
 * The forge-store row shape:
 * ```json
 * {
 *   "id": "asset_skill_use_exa",
 *   "publisherNamespace": "skaile",
 *   "name": "use-exa",
 *   "version": "1.0.0",
 *   "kind": "skill",
 *   "sha256": "<hex>",
 *   "description": "...",
 *   "license": "MIT",
 *   "category": "knowledge",
 *   "requiresJson": "{\"refs\":[]}",
 *   "filesJson": "{\"paths\":[]}"
 * }
 * ```
 *
 * The canonical `CatalogAsset.id` is built as `<publisherNamespace>/<name>@<version>`.
 * The `manifest` is assembled from both `requiresJson` and `filesJson` (both are optional
 * JSON strings; parse failures fall back to `{}`).
 */
function normalizeRestAsset(raw: unknown): CatalogAsset {
  if (!raw || typeof raw !== "object") {
    throw new CatalogHttpError("Catalog REST response item is not an object", 200);
  }
  const r = raw as Record<string, unknown>;
  const publisher = pickString(r, "publisherNamespace") ?? pickString(r, "publisher") ?? "";
  const name = pickString(r, "name") ?? "";
  const version = pickString(r, "version") ?? "";
  const kind = pickString(r, "kind") ?? "";

  // Build canonical ref as the asset id.
  const id = `${publisher}/${name}@${version}`;

  // Build manifest from requiresJson + filesJson.
  const requiresParsed = tryParseJson(pickString(r, "requiresJson"));
  const filesParsed = tryParseJson(pickString(r, "filesJson"));
  const manifest: Record<string, unknown> = { ...requiresParsed, ...filesParsed };

  const asset: CatalogAsset = {
    id,
    kind,
    publisher,
    name,
    version,
    manifest,
  };

  const sha = pickString(r, "sha256");
  if (sha) asset.sha256 = sha;
  const description = pickString(r, "description");
  if (description) asset.description = description;
  const license = pickString(r, "license");
  if (license) asset.license = license;
  const category = pickString(r, "category");
  if (category) asset.category = category;
  const domainSlug = pickString(r, "domainSlug");
  if (domainSlug) asset.domainSlug = domainSlug;
  const softDeps = refsFrom(tryParseJson(pickString(r, "softDepsJson")));
  if (softDeps.length > 0) asset.softDeps = softDeps;

  return asset;
}

/** Extract soft-dep refs from a parsed `{ refs: string[] }` or bare `string[]`. */
function refsFrom(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === "string");
  if (v && typeof v === "object" && Array.isArray((v as { refs?: unknown }).refs)) {
    return (v as { refs: unknown[] }).refs.filter((x): x is string => typeof x === "string");
  }
  return [];
}

function pickString(r: Record<string, unknown>, key: string): string | undefined {
  const v = r[key];
  return typeof v === "string" ? v : undefined;
}

/**
 * Parse a JSON string, returning an object or `{}` on any failure.
 */
function tryParseJson(s: string | undefined): Record<string, unknown> {
  if (!s) return {};
  try {
    const parsed = JSON.parse(s);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // fall through
  }
  return {};
}

// ---------------------------------------------------------------------------
// Default fetch
// ---------------------------------------------------------------------------

/**
 * Upper bound on a single catalog HTTP request. Without it, a hung or
 * unreachable backend would block `resolve()` / `listAssets()` indefinitely
 * (a dropped connection never yields a TCP RST) and stall the caller.
 */
const CATALOG_REQUEST_TIMEOUT_MS = 10_000;

const defaultFetch: HttpClient = async (url, init) => {
  if (typeof fetch === "undefined") {
    throw new Error("global fetch is not available; pass an httpClient explicitly");
  }
  const requestInit = (init ?? {}) as RequestInit;
  return fetch(url, {
    ...requestInit,
    signal: requestInit.signal ?? AbortSignal.timeout(CATALOG_REQUEST_TIMEOUT_MS),
  });
};
